import { types, flow, getEnv, SnapshotOut } from 'mobx-state-tree';
import * as event from './event';
import { observable } from 'mobx';
import { flatten } from 'easit--utils';
import { IAppEnv } from '../appEnvironment';
import { TopOfferType } from '../api/topOfferType';
import { eventListWithDetailModel } from './eventListWithDetailModel';
import { ISportsBettingApi } from '../api/api';
import { IWebTopBetCategory, protoUtils, IGroupedApiCtx, SportsBettingApiResult, IWebTopBetsWithTs, ISBApiOperationResponse, errorIsCancel, ProgressStateModel, StatisticsType, StatisticsInApiStore, OddsPopularityStatsInApiStore } from 'eTip--common';
import { rootCategoryId } from '../../eTipCommon/models/event';

export const ticketEventsOfTheDayStoreKey = 'ticketEventsOfTheDayStore';
export const ticketEventsOfTheDayStore = types
    .compose(
        eventListWithDetailModel,
        types.model({
            finalRate: types.maybeNull(types.number),
            stakes: types.optional(types.array(types.number), []),
            eventualWinnings: types.optional(types.array(types.number), [])
        })
    )
    .named(ticketEventsOfTheDayStoreKey)
    .views(self => ({
        get eventualWinningsCurrency() {
            return '€';
        },
        finalStake(value: number) {
            return (value / self.finalRate).toFixed(2);
        }
    }))
    .actions(self => {
        const env = getEnv<IAppEnv>(self);

        const setEvents = (items: IWebTopBetCategory[], withDefaults: boolean) => {
            self.events = observable(
                flatten(
                    items.map(i => {
                        return i.Base.Events.map((e, eventIdx) =>
                            event.EventDataStore.create(<SnapshotOut<typeof event.EventDataStore>>{
                                Id: e.Id,
                                Name: e.Name,
                                Date: protoUtils.protoTimeStamp2Date(e.Date).getTime(),
                                GraphicsPresentationID: e.GraphicsPresentationId,
                                DetailsCount: e.DetailCount,
                                CategoryPath: e.CategoryPath,
                                Statistics: e.StatisticsExternalId?.length ? StatisticsInApiStore.create(<SnapshotOut<typeof StatisticsInApiStore>>{ id: e.StatisticsExternalId, type: StatisticsType.BRMatch }) : null,
                                GameGroups: self.createGameGroups(e.Id, e.Name, e.GameGroups, withDefaults && eventIdx === 0 && e.Id),
                                ShowDayOfDate: true,
                                EventTvs: e.Tvs && e.Tvs.length
                                    ? observable(
                                        e.Tvs.map(e => {
                                            return { id: e.Id, name: e.Name, date: protoUtils.protoTimeStamp2Date(e.Date).getTime() };
                                        })
                                    )
                                    : undefined,
                                CategoryId: undefined,
                                Info: e.Info,
                                MarketingMessage: e.MarketingMessage,
                                remainingSeconds: null,
                                SuperOddsPopularityStats: observable(e.SuperOddsPopularityStats.map(x => OddsPopularityStatsInApiStore.create(<SnapshotOut<typeof OddsPopularityStatsInApiStore>>{ tipId: x.TipID, name: x.Name, percentage: x.Percentage })))
                            }, env)
                        );
                    }
                    )
                ));
        };

        let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
        const loadAsync: (params: { eventId?: number; detailId?: number }, withDefaults: boolean) => Promise<void> = flow(function* (params: { eventId?: number; detailId?: number }, withDefaults: boolean) {
            self.overallState = ProgressStateModel.Loading;
            try {
                if (ctx && ctx.isOpen()) {
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                const r: ISBApiOperationResponse<IWebTopBetsWithTs> = yield ctx.api.getWebTopBetsAsync(TopOfferType.PreparedTicket, null, self.lastReloadTime);
                if (r.Result === SportsBettingApiResult.Success && r.ReturnValue != null) {
                    env.loggingService.info('getWebTopBets', r.ReturnValue);
                    self.resetDefaults();
                    self.finalRate = r.ReturnValue.TopBets.TicketOfDayRate;
                    self.stakes = observable(r.ReturnValue.TopBets.Stakes ? r.ReturnValue.TopBets.Stakes : []);
                    self.eventualWinnings = observable(r.ReturnValue.TopBets.PotentialWinnings ? r.ReturnValue.TopBets.PotentialWinnings : []);
                    setEvents(r.ReturnValue.TopBets.Categories, withDefaults);
                    self.lastReloadTime = r.ReturnValue.Timestamp;
                    if (params.eventId && params.detailId) {
                        self.setDefaults({ eventId: params.eventId, isLive: false, categoryId: rootCategoryId, detailId: params.detailId });
                    }
                    if (self.defaults) {
                        self.showDetailAsync({ eventKey: `${self.defaults.eventId}c${self.defaults.categoryId}`, isLive: false }, false);
                        //not waiting to finish, it may ignore some errors
                    }
                    else {
                        self.hideDetail();
                    }
                    ctx = null;
                }
                self.overallState = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in top bets list loading.', error);
                self.overallState = ProgressStateModel.Failed;
            }
        });

        const reloadAsync: (params: { eventId?: number; detailId?: number }, withDefaults: boolean) => Promise<void> = flow(function* (params: { eventId?: number; detailId?: number }, withDefaults: boolean) {
            try {
                if (ctx && ctx.isOpen()) {   //TODO incorrect, reload must not cancel full load (see e.g. topBetsBase)
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                const r: ISBApiOperationResponse<IWebTopBetsWithTs> = yield ctx.api.getWebTopBetsAsync(TopOfferType.PreparedTicket, null, self.lastReloadTime);
                if (r.Result === SportsBettingApiResult.Success) {

                    let topBets = r.ReturnValue.TopBets;

                    self.resetDefaults();
                    self.finalRate = topBets.TicketOfDayRate;
                    self.stakes = observable(topBets.Stakes ? topBets.Stakes : []);
                    self.eventualWinnings = observable(topBets.PotentialWinnings ? topBets.PotentialWinnings : []);
                    setEvents(topBets.Categories, withDefaults);
                    self.lastReloadTime = r.ReturnValue.Timestamp;
                    if (params.eventId && params.detailId) {
                        self.setDefaults({ eventId: params.eventId, isLive: false, detailId: params.detailId, categoryId: rootCategoryId });
                    }
                    if (self.defaults) {
                        self.showDetailAsync({ eventKey: `${self.defaults.eventId}c${self.defaults.categoryId}`, isLive: false }, false);
                        //not waiting to finish, it may ignore some errors
                    }
                    else {
                        self.hideDetail();
                    }
                    ctx = null;
                } else if (self.overallState === ProgressStateModel.Success && self.detail) {
                    self.reloadDetailAsync(true);
                    //not waiting for finish, it may ignore some errors
                }
                //reload must not change state  self.overallState = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in top bets list loading.', error);
            }
        });

        return {
            loadAsync,
            reloadAsync
        };
    });
